import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Stack, Typography } from '@mui/material';
import { cloneDeep, get, set } from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import LoadingBackdrop from 'src/components/ui-component/loading/LoadingBackdrop';
import MuiSnackbar from 'src/components/ui-component/mui-snackbar/MuiSnackbar';
import { useAuthentication } from 'src/context/AuthContext';
import { RegisterUserAndOrganisation, SendForgotPasswordMail } from 'src/lib/api/AuthUser';
import GetRecord from 'src/lib/api/GetRecord';
import GetRecords from 'src/lib/api/GetRecords';
import { PostRecord } from 'src/lib/api/PostRecord';
import { APIS } from 'src/lib/constants/apis';
import { BENUTZERDATEN_PORTAL_ROLLE, CUSTOM_PATH, NOT_FOUND_ERROR_MESSAGE } from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { getFieldNamefromDataPath, removeDataKeys, removeNullDeep } from 'src/utils/helper';
import AddPersonAndUserValidator from './AddPersonAndUserValidator';

// import schema
import PersonSchema from 'src/lib/paths/person_FULL_Paths.json';
import UserSchema from 'src/lib/paths/user_Paths.json';
const P = PersonSchema.attributes;
const UP = UserSchema.attributes;
const BASIS_BENUTZERNAME_PATH = P.Organisation.schema.attributes.Mailbenachrichtung_Praefix_Fuer_Benutzernamen.path;
const USERNAME_SUFFIX_LENGTH = 3;
const SEND_FORGOT_PASSWORD_DELAY_MS = 2000;

// const PERSON_EXIST_ALREADY_MESSAGE = 'Person als Ansprechpartner*in existiert bereits im System';

const AddPerson: FC<{ handleToggleActiveShowCase: () => void; setCreatePersonSuccess: (value: boolean | null) => void; personId?: number }> = ({
	personId,
	handleToggleActiveShowCase,
	setCreatePersonSuccess,
}) => {
	const { authenticatedData } = useAuthentication();
	const [loading, setLoading] = useState(false);
	const [formValues, setFormValues] = useState({});
	const [formValuesError, setFormValuesError] = useState({});
	const [formErrorMessage, setFormErrorMessage] = useState('');
	const handleUpdateFormValues = (key: string, value: any) => {
		let formValuesCopy = cloneDeep(formValues);
		set(formValuesCopy, key, value);
		let errorObject = cloneDeep(formValuesError);

		delete errorObject[key];
		setFormValues(formValuesCopy);
		setFormValuesError(errorObject);
	};

	const getNextGeneratedUsername = (prefix: string, usernames: string[]): string | null => {
		const normalizedPrefix = (prefix || '').trim();
		if (!normalizedPrefix) return null;

		let highestSuffix = 0;

		for (const username of usernames) {
			const normalizedUsername = (username || '').trim();
			if (!normalizedUsername.startsWith(`${normalizedPrefix}_`)) continue;

			const suffixPart = normalizedUsername.slice(normalizedPrefix.length + 1);
			if (!suffixPart) continue;

			const parsedSuffix = Number(suffixPart);
			if (!Number.isNaN(parsedSuffix) && parsedSuffix > highestSuffix) {
				highestSuffix = parsedSuffix;
			}
		}

		const nextSuffix = String(highestSuffix + 1).padStart(USERNAME_SUFFIX_LENGTH, '0');
		return `${normalizedPrefix}_${nextSuffix}`;
	};

	const validateFormValues = () => {
		let errorObject = {};

		let errs = AddPersonAndUserValidator.validate(formValues);
		for (let err of errs) {
			set(errorObject, err.path, get(err, 'message'));
		}
		setFormValuesError(errorObject);
		return Object.keys(errorObject).length === 0;
	};

	const handleFormSubmit = async () => {
		// if (get(formValues, 'attributes.hasUser')) {
		await handleCreatePersonAndUser();
		// } else {
		// 	await handleCreatePersonOnly();
		// }
	};

	// const handleCreatePersonOnly = async () => {
	// 	console.log(' creating ONLY person for this values ', formValues);

	// 	if (validateFormValues()) {
	// 		setLoading(true);
	// 		try {
	// 			let personCheckRes = await CheckIfPersonExist(get(formValues, P.Kontaktdaten_E_Mail.path));
	// 			if (get(personCheckRes, 'data', []).length > 0) {
	// 				setFormErrorMessage(PERSON_EXIST_ALREADY_MESSAGE);
	// 			} else {
	// 				// Creating Person
	// 				const persondata = {
	// 					...get(formValues, 'attributes'),
	// 					[getFieldNamefromDataPath(P.Organisation.path)]: get(authenticatedData, 'organisationId', -1),
	// 				};
	// 				let personRes = await PostRecord(APIS.Person, { data: persondata });
	// 				if (personRes) {
	// 					setCreatePersonSuccess(true);
	// 					handleToggleActiveShowCase();
	// 				}
	// 			}
	// 		} catch (err: any) {
	// 			console.error('ERROR: Creating Person ', err);
	// 			setFormErrorMessage(NOT_FOUND_ERROR_MESSAGE);
	// 		} finally {
	// 			setLoading(false);
	// 		}
	// 	}
	// };
	const handleCreatePersonAndUser = async () => {
		if (validateFormValues()) {
			setLoading(true);
			try {
				const persondata = {
					...get(formValues, 'attributes'),
					Portal_Benutzerdaten_Portal_Rolle:
						get(formValues, CUSTOM_PATH.isAdmin, false) ? BENUTZERDATEN_PORTAL_ROLLE.Admin : BENUTZERDATEN_PORTAL_ROLLE.Standard,
					[getFieldNamefromDataPath(P.Organisation.path)]: get(authenticatedData, 'organisationId', -1),
				};
				let personRes = await PostRecord(APIS.Person, { data: persondata });
				if (personRes) {
					const userData = {
						...formValues,
						email: get(formValues, P.Kontaktdaten_E_Mail.path),
					};
					let userRes = await RegisterUserAndOrganisation(
						{},
						userData,
						{},
						true,
						get(personRes, 'data.id'),
						get(authenticatedData, 'organisationId', -1),
					);
					if (userRes) {
						try {
							await new Promise((resolve) => setTimeout(resolve, SEND_FORGOT_PASSWORD_DELAY_MS));
							await SendForgotPasswordMail({
								username: get(formValues, UP.username.path),
							});
						} catch (mailErr: any) {
							console.error('ERROR: Sending password reset email ', mailErr);
						}
						setCreatePersonSuccess(true);
						handleToggleActiveShowCase();
					}
				}
			} catch (err: any) {
				console.error('ERROR: Creating User and Person ', err);
				setFormErrorMessage(NOT_FOUND_ERROR_MESSAGE);
			} finally {
				setLoading(false);
			}
		}
	};

	useEffect(() => {
		const fetchUsernameSuggestion = async () => {
			if (personId) return;

			try {
				setLoading(true);
				const organisationId = get(authenticatedData, 'organisationId', -1);
				const organisationRes = await GetRecord(APIS.Organisation, organisationId);
				const basisPrefix = String(get(organisationRes, `data.attributes.${getFieldNamefromDataPath(BASIS_BENUTZERNAME_PATH)}`, ''));

				const filter = {
					[getFieldNamefromDataPath(P.Organisation.path)]: { id: { $eq: organisationId } },
				};
				const personsRes = await GetRecords(APIS.Person, ['User'], filter);
				const cleanedPersons = removeNullDeep(removeDataKeys(get(personsRes, 'data', [])));
				const existingUsernames: string[] = (cleanedPersons as any[])
					.map((personRecord) => get(personRecord, `${P.User.path}.attributes.${UP.username.path}`, null))
					.filter((username): username is string => typeof username === 'string' && username.trim().length > 0)
					.map((username) => username.trim());

				const generatedUsername = getNextGeneratedUsername(basisPrefix, existingUsernames);
				setFormValues((prev) => {
					const next = cloneDeep(prev);
					set(next, UP.username.path, generatedUsername);
					return next;
				});
			} catch (err: any) {
				console.error('ERROR: Fetching generated username ', err);
				setFormErrorMessage(NOT_FOUND_ERROR_MESSAGE);
			} finally {
				setLoading(false);
			}
		};

		const fetch = async () => {
			try {
				setLoading(true);
				let personRes = await GetRecord(APIS.Person, personId || -1, ['User', 'Organisation']);
				setFormValues(removeNullDeep(removeDataKeys(get(personRes, 'data', {}))));
			} catch (err: any) {
				console.error('ERROR: Fetching Person and User ', err);
				setFormErrorMessage(NOT_FOUND_ERROR_MESSAGE);
			} finally {
				setLoading(false);
			}
		};

		if (personId) {
			fetch();
			return;
		}

		fetchUsernameSuggestion();
	}, [personId, authenticatedData]);
	return (
		<>
			<LoadingBackdrop open={loading} />
			<MuiSnackbar
				showSnackbar={Boolean(formErrorMessage)}
				closeSnackbar={() => setFormErrorMessage('')}
				alertMessage={formErrorMessage}
				error={true}
			/>
			<Box sx={{ mt: '0.625rem' }}>
				<Typography variant='h3' component={'h1'}>
					Personenverwaltung
				</Typography>

				<CreateForm
					formValues={formValues}
					formStruc={formStrucPerson()}
					updateFormValuesFunc={handleUpdateFormValues}
					formErrors={formValuesError}
					api={APIS.Person}
				/>

				<CreateForm
					formValues={formValues}
					formStruc={formStrucUser()}
					updateFormValuesFunc={handleUpdateFormValues}
					formErrors={formValuesError}
					api={APIS.Person}
				/>

				<Stack direction={'row'} justifyContent={'space-between'} alignItems={'flex-end'} sx={{ mt: '2rem' }}>
					<Button variant={'outlined'} onClick={() => handleToggleActiveShowCase()}>
						Abbrechen
					</Button>
					<Button endIcon={<AddIcon />} onClick={handleFormSubmit}>
						Erstellen
					</Button>
				</Stack>
			</Box>
		</>
	);
};

export default AddPerson;

const formStrucPerson = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{ ...P.Personenbezogene_Daten_Anrede, fieldGroupWidth: '30%' },
				{ ...P.Personenbezogene_Daten_Titel, fieldGroupWidth: '70%' },
			],
			userProps: { columnToDivide: 2 },
		},
		{
			fields: [P.Personenbezogene_Daten_Vorname, P.Personenbezogene_Daten_Nachname],
			userProps: { columnToDivide: 2 },
		},
		{
			fields: [P.Kontaktdaten_E_Mail, P.Kontaktdaten_Telefon],
			userProps: { columnToDivide: 2 },
		},
	];

	return struc;
};
const formStrucUser = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [{ ...UP.username, disabled: true }],
			userProps: { columnToDivide: 2 },
		},
		// TODO: Read  isAdmin from const PATHS
		{
			fields: [{ path: 'isAdmin', type: 'boolean', label: 'Diesem Profil Administrative Rechte zuweisen' }],
			userProps: { columnToDivide: 1 },
		},
	];
	return struc;
};
